import { Btn } from "../../components/ui/Button";
import {
  activeHeadline,
  finishedSummary,
  formatElapsed,
  noProgressNote,
  problemSummary,
  queuedDetail,
  stalledMessage,
  startedBy,
  stepSegments,
  workerLabel,
  type Tone,
} from "./refreshStatus";
import type { FrsRefreshStatus, FrsRefreshView } from "./types";

interface Props {
  status: FrsRefreshStatus | null;
  elapsedSeconds: number | null;
  unreachable: boolean;
  result: FrsRefreshView | null;
  onDismissResult: () => void;
}

const TONE_CLASSES: Record<Tone, string> = {
  ok: "border-rs-green/40 bg-rs-green/10",
  info: "border-rs-light-purple/40 bg-rs-purple/10",
  bad: "border-rs-red/50 bg-rs-red/10",
};

function Stepper({ status }: { status: FrsRefreshStatus }) {
  const segments = stepSegments(status.stageIndex, status.stageTotal, status.progressPercent);
  if (segments.length === 0) {
    return <div className="mt-3 h-1.5 w-full rounded-full bg-rs-jet overflow-hidden"><div className="h-full w-1/3 rounded-full bg-rs-green animate-pulse" /></div>;
  }
  return (
    <div className="mt-3 flex gap-1" role="progressbar" aria-valuemin={0} aria-valuemax={segments.length} aria-valuenow={status.stageIndex ?? 0}>
      {segments.map((segment, position) => (
        <div key={position} className="h-1.5 flex-1 rounded-full bg-rs-jet overflow-hidden">
          <div
            className={`h-full rounded-full bg-rs-green ${segment.state === "current" && status.progressPercent === null ? "animate-pulse" : ""}`}
            style={{ width: segment.state === "current" && status.progressPercent === null ? "100%" : `${segment.fill * 100}%` }}
          />
        </div>
      ))}
    </div>
  );
}

export function RefreshBanner({ status, elapsedSeconds, unreachable, result, onDismissResult }: Props) {
  if (status && status.state !== "Idle") {
    const queued = status.state === "Queued";
    const stalled = stalledMessage(status);
    const quiet = noProgressNote(status, elapsedSeconds);
    const who = startedBy(status);
    const headline = activeHeadline(status);
    const detail = queued
      ? queuedDetail(status)
      : [status.stage, status.progressPercent !== null ? `${status.progressPercent}%` : null, status.progressDetail ? `(${status.progressDetail})` : null]
          .filter(Boolean)
          .join(" ");
    return (
      <div role="status" aria-live="polite" className={`mb-5 rounded-lg border px-5 py-4 text-[13px] ${stalled ? TONE_CLASSES.bad : TONE_CLASSES.ok}`}>
        <div className="flex items-start gap-3">
          <span className={`material-symbols-sharp text-rs-green ${queued ? "animate-pulse" : "animate-spin"}`}>{queued ? "hourglass_top" : "sync"}</span>
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-semibold text-white">{headline}</div>
            {detail && <div className="mt-0.5 text-rs-off-white">{detail}</div>}
            <div className="mt-0.5 text-xs text-rs-light-gray">
              {[who, elapsedSeconds !== null ? `${queued ? "Waiting" : "Running"} for ${formatElapsed(elapsedSeconds)}` : null, workerLabel(status)].filter(Boolean).join(" · ")}
            </div>
            {stalled && <div className="mt-1 text-xs text-rs-red">{stalled}</div>}
            {quiet && <div className="mt-1 text-xs text-rs-light-purple">{quiet}</div>}
            {!queued && <p className="m-0 mt-1 text-xs text-rs-light-gray">The registry stays usable while this runs. Facility data may change when it finishes.</p>}
          </div>
        </div>
        {!queued && <Stepper status={status} />}
      </div>
    );
  }

  const failed = status?.lastRun?.status === "Failed" ? status.lastRun : null;
  const dropped = problemSummary(status);
  const summary = result ? finishedSummary(result) : (dropped ?? (failed ? finishedSummary(failed) : null));
  if (summary) {
    return (
      <div role="status" aria-live="polite" className={`mb-5 flex items-start gap-3 rounded-lg border px-5 py-4 text-[13px] ${TONE_CLASSES[summary.tone]}`}>
        <span className={`material-symbols-sharp ${summary.tone === "bad" ? "text-rs-red" : "text-rs-green"}`}>
          {summary.tone === "bad" ? "error" : summary.tone === "info" ? "info" : "check_circle"}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold text-white">{summary.title}</div>
          <div className="mt-0.5 text-rs-off-white break-words">{summary.text}</div>
        </div>
        {result && (
          <Btn variant="ghost" onClick={onDismissResult}>
            Dismiss
          </Btn>
        )}
      </div>
    );
  }

  if (unreachable) {
    return <p className="mb-5 text-xs text-rs-light-gray">Could not check whether a refresh is running. Retrying.</p>;
  }
  return null;
}
